import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useSAL6020_R01Store } from "./Store_SAL6020_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { settingSAL6020_R01CellClasses } from "src/utils/grid/GridCellStylesContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultSAL6020_R01DetailDto, SAL6020_R01DetailDto } from "./Dto_SAL6020_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'SAL6020_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL6020_R01DetailDto> | null>;
}

export const DetailTable_SAL6020_R01 = (props: Props) => {

	const { gridRef } = props;

	const { 
			SAL6020_R01DetailList, setSAL6020_R01DetailList, 
			selectedSAL6020_R01DetailList, setSelectedSAL6020_R01DetailList, removeSelectedSAL6020_R01DetailList, addSelectedSAL6020_R01DetailList, initSelectedSAL6020_R01DetailList,
			deletedSAL6020_R01DetailList, setDeletedSAL6020_R01DetailList, addDeletedSAL6020_R01DetailList, removeDeletedSAL6020_R01DetailList,
			insertSAL6020_R01DetailList, updateSAL6020_R01DetailList, removeSAL6020_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalSAL6020_R01DetailList, setOriginalSAL6020_R01DetailList,
			detailScrollTop, setDetailScrollTop,
		} = useSAL6020_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const [clickable, setClickable] = useState(true);

	const { unitList } = useCommonCodeStore();

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div에 대한 ref 생성

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SAL6020_R01DetailList,
		defaultGridData: defaultSAL6020_R01DetailDto,
		selectedRow: selectedSAL6020_R01DetailList,
		originalGridData: originalSAL6020_R01DetailList,
		updateRow: (index: number, data: any) => updateSAL6020_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		modelNo: '모델번호',
		itemQty: '수량',
		unit: '단위',
		itemPrice: '단가',
		dcAmt: '할인금액',
		itemAmt: '금액',
	}

	// 품목 검색 모달 설정은 useDetailTableHandlers에서 처리
  
	const createCellClass = (params: any) => {	
		return settingSAL6020_R01CellClasses(params, params.colDef.colId!, selectedSAL6020_R01DetailList.includes(params.data.valueIndex), deletedSAL6020_R01DetailList.includes(params.data.valueIndex));
	}

	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'modelNo', 'estimateUnit', 'estimateQty', 'estimatePrice', 'dcRate', 'dcAmt'];
	const defaultCodeSearchColumns = ['itemCd', 'itemNm', 'itemSpec', 'modelNo', 'unit'];

	const commonInputColumns = [ 'itemCd', 'estimateQty', 'estimatePrice', 'dcAmt' ];

	const baseColumnDefs: ColDef<SAL6020_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: SAL6020_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: SAL6020_R01DetailDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedSAL6020_R01DetailList,
						selectedList: selectedSAL6020_R01DetailList,
						dataList: SAL6020_R01DetailList
					}),
				(rowIndex: number, e: React.MouseEvent) => InsertRowHandler({
					event: e,
					dataList: SAL6020_R01DetailList,
					rowData: SAL6020_R01DetailList[rowIndex],
					insertRow: insertSAL6020_R01DetailList,
					defaultRowData: defaultSAL6020_R01DetailDto,
					setClickable
				}),
				(rowData) => RemoveRowHandler( 
					gridRef,
					SAL6020_R01DetailList, 
					rowData,
					setClickable, 
					removeSAL6020_R01DetailList, 
					(valueIndex) => addDeletedSAL6020_R01DetailList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 77,	
			cellClass: (params: any) => createCellClass(params),
			editable: true,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 161,
			cellClass: (params: any) => createCellClass(params),
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 127,
			cellClass: (params: any) => createCellClass(params),
			editable: false,
		},
		{
			headerName: '모델번호', colId: 'modelNo', field: 'modelNo', width: 130,
			cellClass: (params: any) => createCellClass(params),
			editable: false,
		},
		
		{
			headerName: '단위', colId: 'estimateUnit', field: 'estimateUnit', width: 60,
			cellClass: (params: any) => createCellClass(params),
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '수량', colId: 'estimateQty', field: 'estimateQty', width: 60,
			cellClass: (params: any) => createCellClass(params),
			editable: (params) => SAL6020_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},

		{
			headerName: '단가', colId: 'estimatePrice', field: 'estimatePrice', width: 65,
			cellClass: (params: any) => createCellClass(params),
			editable: (params) => SAL6020_R01DetailList[params.node.rowIndex!] !== undefined,
			valueGetter: (p: any) => p.data?.itemPrice ?? p.data?.itemCost ?? p.data?.estimatePrice ?? 0,
			valueSetter: (p: any) => { p.data.estimatePrice = Number(p.newValue) || 0; return true; },
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => (params.value ?? 0).toLocaleString()
		},
		{
			headerName: '할인금액', colId: 'dcAmt', field: 'dcAmt', width: 65,
			cellClass: (params: any) => createCellClass(params),
			editable: (params) => SAL6020_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '금액', colId: 'itemAmt', field: 'itemAmt', width: 96,
			editable: false,
			cellClass: (params: any) => createCellClass(params),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 100,
			cellClass: (params: any) => createCellClass(params),
			editable: false,
		},
		// ...IndexParameterCell(),
	], [unitList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL6020_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL6020_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);
	

	const handleRowClick = useRowSelectionHandler({
		dataList: SAL6020_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedSAL6020_R01DetailList,
		deletedList: deletedSAL6020_R01DetailList,
		addSelected: addSelectedSAL6020_R01DetailList,
		removeSelected: removeSelectedSAL6020_R01DetailList,
		clickable, setClickable
	});

	// === DetailTable 내장 핸들러 로직 ===
	// 공통 코드 검색 훅 사용 (공통 모달 방식)
const { searchExactCode } = useCodeSearch({});

	// 모듈별 설정
	const SAL6010R01Config = createModuleConfig.SAL6010();

	// 공통 검색 함수를 설정에 주입 (품목 카테고리)
	const fieldConfigWithSearch = {
		...SAL6010R01Config.fieldConfig,
		codeSearchFields: {
			...SAL6010R01Config.fieldConfig.codeSearchFields,
			itemCd: {
				...SAL6010R01Config.fieldConfig.codeSearchFields!.itemCd,
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess)
			}
		}
	};

	const SAL6010_FIELD_MAPPING = {
		qtyField: 'estimateQty',
		priceField: 'itemPrice',
		dcField: 'dcAmt', 
		amountField: 'itemAmt',
		preventNegative: true   
	};

	// SAL6010 용 금액 계산 함수
	const calculateAmount = (data: SAL6020_R01DetailDto): SAL6020_R01DetailDto => {
		return calculateDetailAmount(data, SAL6010_FIELD_MAPPING);
	};

	// 공통 훅 사용
	const detailTableHandlers = useCommonDetailTableHandlers<SAL6020_R01DetailDto>({
		detailList: SAL6020_R01DetailList,
		updateDetailList: updateSAL6020_R01DetailList,
		originalDetailList: originalSAL6020_R01DetailList,
		setCodeSearchModalProps,
		gridRef,
		fieldConfig: fieldConfigWithSearch,
		codeSearchConfig: SAL6010R01Config.codeSearchConfig,
		calculateAmount,
	});

	const cellClickActions: CustomCellClickAction<SAL6020_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field) => commonInputColumns.includes(field),
			action: (e) => {
				e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
			}
		}
	], []);
	const handleCellClick = CreateCellClickHandler(SAL6020_R01DetailList, 'valueIndex', cellClickActions);

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 품목 코드 더블클릭으로 품목 검색 모달 열기
	 * - 키다운 네비게이션 (엔터 시 수량 필드로 이동, 방향키 네비게이션)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<SAL6020_R01DetailDto>({
		detailList: SAL6020_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',      // 품목코드
					itemNm: 'itemNm',      // 품명
					itemSpec: 'itemSpec',  // 규격
					modelNo: 'modelNo',    // 품번
					itemUnit: 'orderUnit', // 단위
					itemCost: 'orderPrice' // 단가
				},
				clearFields: {
					// 품목 검색 시 초기화할 필드들
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "", orderUnit: "",
					orderPrice: 0, dcRate: 0, dcAmt: 0, orderAmt: 0
				},
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess) // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		keyDownConfig: {
			qtyField: 'orderQty',        // 엔터 키 시 수량 필드로 이동
			enableArrowNavigation: true  // 방향키 네비게이션 활성화
		}
	});
	const handleCellDoubleClick = cellEventHandlers.handleCellDoubleClick;

	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<SAL6020_R01DetailDto>) => {
		if (isRowDraggingRef.current) return;
		postSortHandler(params, SAL6020_R01DetailList, originalSAL6020_R01DetailList, setSAL6020_R01DetailList);
	}, [SAL6020_R01DetailList, originalSAL6020_R01DetailList, ]);


	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = SAL6020_R01DetailList
			.filter(item => !deletedSAL6020_R01DetailList.includes(item.valueIndex.toString()))
			.reduce((sum, item) => sum + (Number(item.estimateQty) || 0), 0);
		const totalAmt : number = SAL6020_R01DetailList
			.filter(item => !deletedSAL6020_R01DetailList.includes(item.valueIndex.toString()))
			.reduce((sum, item) => sum + (Number(item.itemAmt) || 0), 0);

		return [{
			itemCd: SAL6020_R01DetailList.length.toLocaleString() + "건",
			estimateQty: totalQty.toLocaleString(),
			itemAmt: totalAmt.toLocaleString()
		}];	  
	}, [SAL6020_R01DetailList, deletedSAL6020_R01DetailList]);

	useEffect(() => { ensureSelectedRow(); }, [SAL6020_R01DetailList, selectedSAL6020_R01DetailList]);

	return (
		<GridContextWrapper<SAL6020_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setSAL6020_R01DetailList}
			initSelected={initSelectedSAL6020_R01DetailList}
			defaultRowData={defaultSAL6020_R01DetailDto}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>

			<Grid_Excel<SAL6020_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}

				rowClassRules={createRowClassRules<SAL6020_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedSAL6020_R01DetailList,
					deletedList: deletedSAL6020_R01DetailList
				})}

				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellValueChanged={detailTableHandlers.handleCellChange}
				onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				pinnedBottomRowData={pinnedBottomRowData}
                rowDragManaged={true}
                rowDragEntireRow={true}
				onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                onRowDragEnd={(e: any) => {
                    const api = e.api;
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    const reordered: SAL6020_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,
                            orderIndexSeq: 0,
                        });
                    });
                    setSAL6020_R01DetailList(reordered);
					isRowDraggingRef.current = false;
                }}

				onPostSortRows={handlePostSort}
                columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	)
}
